import React, { FC, useEffect, useState } from 'react';

import { VulnerablePackage } from '@ariksa/inventory-core/api';
import { Box, useDisclosure } from '@chakra-ui/react';
import { isEqual } from 'lodash';
import { useVulnerabilityCvesById } from 'services/Vulnerability/useVulnerabilityResourceService';

import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { Sorted } from 'components/DataDisplay/NewTable/types';
import { useVulnerabilityTableColumns } from 'containers/Visibility/Vulnerabilities/Components/hooks/useVulnerabilityTableColumns';
import { CVEDetailsDrawer } from 'containers/Visibility/Vulnerabilities/Components/VulnerabilityTable/CVE/CVEDetailsDrawer';
import { HostsDrawer } from 'containers/Visibility/Vulnerabilities/Components/VulnerabilityTable/HostsDrawer';
import { VulnerabilityViewByTabs } from 'containers/Visibility/Vulnerabilities/types';

interface Props {
  currentRecord: VulnerablePackage;
  severity: Record<string, any>;
  hasPublicExploit: Record<string, any>;
  searchTerm: string;
}

export const CvesById: FC<Props> = props => {
  const { currentRecord, severity, hasPublicExploit, searchTerm } = props;
  const { cvesById } = useVulnerabilityCvesById();
  const {
    packagesCVEColumns,
    hostsDisclosure,
    currentCVE,
  } = useVulnerabilityTableColumns();
  const [sortByField, setSortByField] = useState<Sorted>({
    sortField: 'severity',
    sortOrder: 'desc',
  });
  const [currentRow, setCurrentRow] = useState<Record<string, any>>({});
  const cveDetailsDisclosure = useDisclosure();

  useEffect(() => {
    cvesById.sync({
      sortBy: sortByField.sortField,
      sortOrder: sortByField.sortOrder,
      cVEByIDRequest: {
        package_name: currentRecord?.package_name,
        package_version: currentRecord?.installed_version,
      },
      severity: severity?.value,
      exploitable: hasPublicExploit?.value,
      searchTerm,
    });
  }, [
    currentRecord,
    cvesById,
    sortByField,
    severity,
    searchTerm,
    hasPublicExploit,
  ]);

  return (
    <Box flex={1} h="full">
      <Table
        data={cvesById.data?.items ?? []}
        isError={cvesById.isError}
        columns={packagesCVEColumns}
        isLoading={cvesById.isLoading}
        styles={{ header: { position: 'relative', zIndex: 800 } }}
        sortBy={sortByField}
        onSort={sortInfo => {
          if (!isEqual(sortByField, sortInfo)) {
            setSortByField(sortInfo);
          }
        }}
        onRowClick={row => {
          cveDetailsDisclosure.onOpen();
          setCurrentRow(row);
        }}
      />
      {hostsDisclosure.isOpen && (
        <HostsDrawer
          row={currentCVE}
          isOpen={hostsDisclosure.isOpen}
          onClose={hostsDisclosure.onClose}
          selectedTab={VulnerabilityViewByTabs.CVE}
          fromTab={VulnerabilityViewByTabs.Package}
        />
      )}
      {cveDetailsDisclosure.isOpen && (
        <CVEDetailsDrawer
          isOpen={cveDetailsDisclosure.isOpen}
          onClose={cveDetailsDisclosure.onClose}
          currentRecord={currentRow}
        />
      )}
    </Box>
  );
};
